import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import { isEmpty, get } from "lodash";
import {
  errorIcon,
  menuDeleteIcon,
  menuEditIcon,
  serviceIcon,
} from "../../assets/icons";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { deleteService } from "../DataModels";
import { PopOver } from "../../components/atoms";
import {
  EmptyLayout,
  SearchBar,
  Table,
  Footer,
  Alert,
} from "../../components/molecules";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import {
  POPOVER_BUTTONS,
  FOOTER_CONSTANTS,
  EMPTY_ARRAY,
  INDEX,
} from "../../constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import {
  selectMulesoftServices,
  setSelectedMulesoftEndpoint,
  setSelectedIntegrationId,
} from "./index";
import { validateServiceDeletion } from "../../helpers";

const { DELETE, EDIT } = POPOVER_BUTTONS;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { SERVICES_TITLE, SERVICES_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_SERVICE, FETCH_SERVICES } = SPINNER_NAMES;
const { FIRST } = INDEX;
const { DELETE_MODAL } = MODALS;
const SINGLE_ENDPOINT = 1;

const MulesoftServices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { integrationId } = useParams();
  const allMulesoftServices = useSelector(selectMulesoftServices);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [filteredServices, setFilteredServices] = useState(EMPTY_ARRAY);
  const [services, setServices] = useState(EMPTY_ARRAY);
  const [alertData, setAlertData] = useState();

  useEffect(() => {
    integrationId && dispatch(setSelectedIntegrationId(integrationId));
  }, [integrationId, dispatch]);

  useEffect(() => {
    if (allMulesoftServices) {
      setServices(allMulesoftServices);
      setFilteredServices(allMulesoftServices);
    }
  }, [allMulesoftServices]);

  const buttonFormatter = (row) => (
    <div onClick={(e) => e.stopPropagation()}>
      <PopOver
        options={popOverOptions(row)}
        classes="right-6 -bottom-3"
        data={row}
      />
    </div>
  );

  const endpointFormatter = (row) => {
    if (row.endpoints?.length) {
      const endpoints = row.endpoints.map(({ method, url }) => ({
        name: `Method: ${method}\nURL: ${url}`,
      }));

      if (endpoints.length === SINGLE_ENDPOINT)
        return <span>{endpoints[FIRST].name}</span>;
      return (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span>{endpoints.length} Endpoint</span>
          <div onClick={(e) => e.stopPropagation()}>
            <PopOver
              options={endpoints}
              classes="right-6 -bottom-3"
              data={row}
              icon="info"
            />
          </div>
        </div>
      );
    }
  };

  const nameFormatter = ({ name, isValid }) => (
    <div className="flex justify-between">
      <p>{name}</p>
      <img
        alt="error"
        className={classNames("w-4", {
          hidden: isValid,
        })}
        src={errorIcon}
      />
    </div>
  );

  const columns = [
    {
      text: "Name",
      formatter: nameFormatter,
    },
    {
      text: "Endpoints",
      formatter: endpointFormatter,
    },
    {
      width: 70,
      formatter: buttonFormatter,
    },
  ];

  const handleOnEditService = (row) => {
    const mulesoftEndpoint = get(row, "endpoints[0]", {});
    dispatch(setSelectedMulesoftEndpoint(mulesoftEndpoint));
    navigate(`/mulesoft/${integrationId}/services/${row.id}/details`);
  };

  const handleDeleteService = useCallback(
    (row) => {
      dispatch(deleteService(row.id)).then((res) => {
        if (res.payload) {
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch]
  );

  const popOverOptions = (row) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      handleOnClick: () => handleOnEditService(row),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () =>
        handleDelete(row, setAlertData, setShouldShowDeleteModal),
    },
  ];

  const handleDelete = (row, setAlertData, setShouldShowDeleteModal) => {
    if (isEmpty(row.endpoints)) {
      dispatch(setSelectedModal(DELETE_MODAL));
      setShouldShowDeleteModal(true);
      dispatch(setItemToBeDeleted(row));
    } else validateServiceDeletion(row, setAlertData);
  };

  const handleOnSearch = useCallback(
    (value) => {
      setFilteredServices(
        services.filter((service) =>
          service.name.toLowerCase().includes(value.toLowerCase())
        )
      );
    },
    [services]
  );

  const generateService = () => {
    navigate(`/mulesoft/${integrationId}/generate`, {
      replace: true,
    });
  };

  const renderServices = () =>
    filteredServices.length ? (
      <>
        <Table
          data={filteredServices}
          columns={columns}
          className="Bordered"
          onRowClick={handleOnEditService}
        />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteService}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Service"
            spinner={DELETE_SERVICE}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Mulesoft Service"
        subTitle="You can get started by generating a new service."
        buttonText="Generate Service from Mulesoft"
        image={serviceIcon}
        handleOnClick={generateService}
      />
    );

  return (
    <div className="content scrollbar overflow-auto p-6 flex-1 flex flex-col">
      <Helmet>
        <title>Services | TavaJS</title>
      </Helmet>
      {alertData && (
        <div className="m-2">
          <Alert
            type={alertData.type}
            message={alertData.message}
            handleOnClose={setAlertData}
          />
        </div>
      )}
      <div className="flex-col xs:flex-row flex mb-4 gap-4">
        <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_SERVICES}>{renderServices()}</Spinner>
        <Footer title={SERVICES_TITLE} subtitle={SERVICES_SUBTITLE} />
      </div>
    </div>
  );
};

export default MulesoftServices;
